(() => {
  if (customElements.get('yahav-judgeme-featured')) return;

  const text = (element) => element?.textContent.trim() || '';
  const score = (element) => Number(element.querySelector('.jdgm-rev__rating')?.dataset.score);
  // Mostly-capitals text reads as shouting in a showcase quote.
  const shouting = (value) => {
    const letters = value.replace(/[^A-Za-z]/g, '');
    return letters.length > 0 && letters.replace(/[^A-Z]/g, '').length / letters.length > 0.6;
  };
  const usable = (element) => text(element.querySelector('.jdgm-rev__author')) &&
    text(element.querySelector('.jdgm-rev__body')) &&
    Number.isInteger(score(element)) && score(element) >= 1 && score(element) <= 5;

  // Store-wide reviews shown on products without their own. Only 5-star reviews with a real sentence,
  // and none about the shipping add-on. The product ID picks one, so each product keeps its own review.
  const storeReview = (template, productId, offset = 0) => {
    const reviews = [...(template?.content.querySelectorAll('.jdgm-rev[data-review-id]') || [])].filter(element =>
      usable(element) && score(element) === 5 &&
      text(element.querySelector('.jdgm-rev__body')).length >= 30 && !shouting(text(element.querySelector('.jdgm-rev__body'))) &&
      !/shipping/i.test(text(element.querySelector('.jdgm-rev__prod-link'))))
      // One review per reviewer, so the two quotes on a page never share a name.
      .filter((element, index, list) => list.findIndex(other =>
        text(other.querySelector('.jdgm-rev__author')).toLowerCase() === text(element.querySelector('.jdgm-rev__author')).toLowerCase()) === index);
    if (!reviews.length) return null;
    const id = Number(productId) || 0;
    return reviews[(id + offset) % reviews.length];
  };

  class FeaturedReview extends HTMLElement {
    connectedCallback() {
      const target = this.querySelector('[data-review-card]');
      if (!target || target.childElementCount) return;
      const review = this.manualReview() || this.productReview() || this.storeReview();
      if (review) this.render(target, review);
    }

    // A review entered in the product's custom.review_* metafields always wins.
    manualReview() {
      const fallback = this.querySelector('[data-review-fallback]')?.content.firstElementChild;
      const name = fallback?.dataset.name?.trim();
      const body = text(fallback?.querySelector('[data-body]'));
      const rating = Number(fallback?.dataset.rating);
      if (!name || !body || !Number.isInteger(rating) || rating < 1 || rating > 5) return null;
      const avatar = fallback.querySelector('img');
      return { name, body, rating, verified: fallback.dataset.verified === 'true', avatar: avatar && (avatar.getAttribute('data-src') || avatar.getAttribute('src')), source: 'manual' };
    }

    productReview() {
      const source = this.querySelector('[data-review-html]')?.content;
      const reviews = [...(source?.querySelectorAll('.jdgm-rev[data-review-id]') || [])].filter(usable);
      // Prefer confirmed purchases, then customer media, preserving Judge.me order.
      const verifiedReviews = reviews.filter(item => item.dataset.verifiedBuyer === 'true');
      const candidates = verifiedReviews.length ? verifiedReviews : reviews;
      const review = candidates.find(item => item.querySelector('.jdgm-rev__pic-img')) || candidates[0];
      if (!review) return null;
      const avatar = review.querySelector('.jdgm-rev__icon img');
      const photo = review.querySelector('.jdgm-rev__pic-img');
      return {
        name: text(review.querySelector('.jdgm-rev__author')),
        body: text(review.querySelector('.jdgm-rev__body')),
        rating: score(review),
        verified: review.dataset.verifiedBuyer === 'true',
        avatar: avatar && (avatar.getAttribute('data-src') || avatar.getAttribute('src')),
        photo: photo && (photo.getAttribute('data-src') || photo.getAttribute('src')),
        source: 'product'
      };
    }

    // Store reviews carry a picture of the product they were written about, so no photo is shown here.
    storeReview() {
      const review = storeReview(this.querySelector('[data-store-reviews]'), this.dataset.productId);
      if (!review) return null;
      return {
        name: text(review.querySelector('.jdgm-rev__author')),
        body: text(review.querySelector('.jdgm-rev__body')),
        rating: score(review),
        verified: review.dataset.verifiedBuyer === 'true',
        source: 'store'
      };
    }

    render(target, review) {
      const { name, body, rating, verified } = review;
      const make = (tag, className, content) => {
        const node = document.createElement(tag);
        node.className = className;
        if (content) node.textContent = content;
        return node;
      };
      const person = make('div', 'yp__quote-person');
      const identity = make('div', '');
      identity.append(make('span', 'yp__quote-name', name));
      if (verified) {
        const badge = make('span', 'yp__quote-verified', 'Verified Buyer ');
        badge.append(this.querySelector('[data-verified-icon]').content.cloneNode(true));
        identity.append(badge);
      }
      if (review.avatar && /^(https:)?\/\//i.test(review.avatar)) {
        const holder = make('span', 'yp__avatar');
        const image = make('img', '');
        image.src = review.avatar;
        image.alt = `Photo of ${name}`;
        image.loading = 'lazy';
        image.width = image.height = 46;
        image.addEventListener('error', () => holder.remove(), { once: true });
        holder.append(image);
        person.append(holder);
      }
      person.append(identity);
      const quote = make('blockquote', 'yp__quote-card');
      const stars = make('span', 'yp__quote-stars');
      for (let i = 1; i <= 5; i++) {
        const star = make('img', '');
        star.src = i <= rating ? this.dataset.starFilled : this.dataset.starOutline;
        star.alt = '';
        star.width = star.height = 22;
        stars.append(star);
      }
      stars.setAttribute('role', 'img');
      stars.setAttribute('aria-label', `${rating} out of 5 stars`);
      quote.append(stars, make('p', '', body));
      if (review.photo && /^https:\/\//i.test(review.photo)) {
        const image = make('img', 'yp__review-photo');
        image.src = review.photo;
        image.alt = `Review photo shared by ${name}`;
        image.loading = 'lazy';
        image.width = 100;
        image.height = 100;
        image.addEventListener('error', () => image.remove(), { once: true });
        quote.append(image);
      }
      if (review.source === 'product') {
        const link = make('a', 'yp__review-source', 'Read product reviews on Judge.me →');
        link.href = '#judgeme_product_reviews';
        quote.append(link);
      } else if (review.source === 'store' && this.dataset.storeReviewsUrl) {
        const link = make('a', 'yp__review-source', 'Read all store reviews on Judge.me →');
        link.href = this.dataset.storeReviewsUrl;
        quote.append(link);
      }
      target.append(person, quote);
      this.hidden = false;
    }
  }

  customElements.define('yahav-judgeme-featured', FeaturedReview);
})();
